/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICommerceApiSettings,
    ICreateActionContext,
    IGeneric
} from '@msdyn365-commerce/core';
import { OrgUnitLocation, SearchArea } from '@msdyn365-commerce/retail-proxy';
import { getOrgUnitLocationsByAreaAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/OrgUnitsDataActions.g';
import { buildCacheKey, QueryResultSettingsProxy } from '@msdyn365-commerce-modules/retail-actions';

/**
 * Interface of Org Unit Locations.
 */
interface IOrgUnitLocations {
    orgUnitLocations: OrgUnitLocation[];
}

const searchArea: SearchArea = {
    Radius: Math.max(),
    DistanceUnitValue: 0 // 0 is miles
};

/**
 * Calls the Retail API and returns the orgUnitLocations.
 * @returns - List Org Unit Locations.
 */
const getOrgUnitLocations = () => async (ctx: IActionContext): Promise<OrgUnitLocation[]> => {
    return getOrgUnitLocationsByAreaAsync(
        {
            callerContext: ctx,
            queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(ctx)
        },
        searchArea
    );
};

/**
 * Action input.
 */
export class GetOrgUnitLocationsInput implements IActionInput {
    private readonly apiSettings: ICommerceApiSettings;

    constructor(apiSettings: ICommerceApiSettings) {
        this.apiSettings = apiSettings;
    }

    public getCacheKey = () => buildCacheKey('OrgUnitLocations', this.apiSettings);

    public getCacheObjectType = () => 'OrgUnitLocations';

    public dataCacheType = (): CacheType => 'request';
}

/**
 * Creates the input required to make the retail api call.
 * @param inputData - Action Context.
 * @returns - Org Unit Location input.
 */
const createOrgUnitLocationsInput = (inputData: ICreateActionContext<IGeneric<IAny>>) => {
    return new GetOrgUnitLocationsInput(inputData.requestContext.apiSettings);
};

/**
 * Get orgUnitLocations action.
 * @param input - Org UNit Locations Input.
 * @param context - Action Context.
 */
export async function getOrgUnitLocationsAction(
    input: GetOrgUnitLocationsInput,
    context: IActionContext
): Promise<IOrgUnitLocations> {
    if (!context) {
        throw new Error('getOrgUnitLocationsAction - Action context cannot be null/undefined');
    }

    let orgUnitLocations: OrgUnitLocation[] = [];

    try {
        orgUnitLocations = await getOrgUnitLocations()(context);
    } catch (error) {
        throw error;
    }

    return { orgUnitLocations };
}

export const getOrgUnitLocationsActionDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/order-management/get-org-unit-locations',
    action: <IAction<IOrgUnitLocations>>getOrgUnitLocationsAction,
    input: createOrgUnitLocationsInput
});

export default getOrgUnitLocationsActionDataAction;
